import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useApp } from '../store';

const STEP_COUNT = 5;

const TUTORIAL_HIGHLIGHTS: Array<'charges' | 'nav' | undefined> = [
  undefined,
  'charges',
  undefined,
  'nav',
  undefined,
];

export function TutorialOverlay() {
  const { t } = useTranslation(['tutorial', 'common']);
  const { user, setUser, setTutorialOpen, setTutorialHighlight } = useApp();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTutorialHighlight(TUTORIAL_HIGHLIGHTS[step] ?? null);
    return () => setTutorialHighlight(null);
  }, [step, setTutorialHighlight]);

  if (!user) return null;

  const last = step >= STEP_COUNT - 1;
  const hint = t(`tutorial:steps.${step}.hint`, { defaultValue: '' });

  async function finish() {
    setBusy(true);
    try {
      const res = await api.updateProfile({ tutorialSeen: true });
      setUser(res.user);
    } catch {
      setUser({ ...user!, tutorialSeen: true });
    } finally {
      setBusy(false);
      setTutorialHighlight(null);
      setTutorialOpen(false);
    }
  }

  return (
    <div className="absolute inset-0 z-40 flex items-end justify-center bg-black/55 p-3 backdrop-blur-[2px] sm:items-center sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-title"
        className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl border border-[var(--line)] bg-[var(--bg-panel)] shadow-2xl"
      >
        <div className="relative px-5 pb-2 pt-5 sm:px-6 sm:pt-6">
          <div className="mb-4 flex gap-1.5">
            {Array.from({ length: STEP_COUNT }, (_, i) => (
              <span
                key={i}
                className={`h-1 flex-1 rounded-full transition ${
                  i <= step ? 'bg-[var(--accent)]' : 'bg-white/10'
                }`}
              />
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">
            {t('tutorial:guide', { current: step + 1, total: STEP_COUNT })}
          </p>
          <h2
            id="tutorial-title"
            className="mt-1 font-[family-name:var(--font-display)] text-2xl leading-tight text-[var(--accent)]"
          >
            {t(`tutorial:steps.${step}.title`)}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-[var(--ink)]">{t(`tutorial:steps.${step}.body`)}</p>
          {hint ? (
            <p className="mt-3 rounded-2xl border border-[var(--line)] bg-white/[0.03] px-3 py-2.5 text-xs leading-relaxed text-[var(--muted)]">
              {hint}
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-[var(--line)] px-4 py-3 sm:px-5">
          <button
            type="button"
            disabled={busy}
            onClick={() => void finish()}
            className="rounded-xl px-3 py-2 text-xs text-[var(--muted)] hover:text-[var(--ink)] disabled:opacity-50"
          >
            {t('tutorial:skip')}
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setStep((s) => s - 1)}
                className="rounded-xl border border-[var(--line)] px-3 py-2 text-xs font-medium"
              >
                {t('tutorial:back')}
              </button>
            )}
            {!last ? (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="rounded-xl bg-[var(--accent)] px-4 py-2 text-xs font-semibold text-[#1a1408]"
              >
                {t('tutorial:next')}
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => void finish()}
                className="rounded-xl bg-[var(--accent)] px-4 py-2 text-xs font-semibold text-[#1a1408] disabled:opacity-60"
              >
                {busy ? t('common:loadingShort') : t('tutorial:go')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
